import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import css from './CheckedInOptions.css';
import TextHeader from './TextHeader';
import StyledButton from '../common/StyledButton';
import OptionButton from './OptionButton';
import useStarfishAPI from '../hooks/useStarfishAPI';
import useError from '../hooks/useError';
import { urlParams } from 'utils/request';
import {canViewWaitList as canViewWaitListKiosk, isSelfService} from '../utils/kiosk';
import {getTimeInHours} from "../utils/time";

const CheckedInOptions = props => {
  // Declarative callbacks for the starfish API
  const [{error: dequeueError, success: dequeueSuccess}, callDequeueAPI] = useStarfishAPI();
  const [{error: cancelError, success: cancelSuccess}, callCancelAppointmentAPI] = useStarfishAPI();

  // Listen to error and call props.setError whenever there is a change
  useError(dequeueError, props.setError);

  // This API will determine if the group session associated with this check-in needs to be cancelled, and cancel it
  const cancelGroupSession = () => {
    const cancelURL = '../kiosk/removeGroupSessionParticipant.html';
    callCancelAppointmentAPI(cancelURL, {
      method: 'POST',
      body: urlParams({
        studentId: props.studentId,
        appointmentId: props.appointmentId
      })
    });
  };

  useEffect(() => {
    const hasGroupSession = props.appointmentId > 0 && props.scheduleBlockId > 0;
    if ( dequeueSuccess ) {
      // If the student had a group session and checked out successfully, try to cancel the appointment
      // If there was no appointment, skip cancelling and go to confirmation page
      hasGroupSession ? cancelGroupSession() : props.goToCheckoutConfirmation();
    }
  }, [dequeueSuccess, props.appointmentId, props.scheduleBlockId]);

  // If the appointment was cancelled go to confirmation page, pass error if error occurred
  useEffect(() => {
    if ( cancelSuccess ) props.goToCheckoutConfirmation();
    else if ( cancelError ) props.goToCheckoutConfirmation(cancelError);
  }, [cancelSuccess, cancelError]);

  const onCheckoutClick = () => {
    const dequeueURL = '../kiosk/removeFromQueue.html';
    callDequeueAPI(dequeueURL, {
      method: 'POST',
      body: urlParams({
        kioskId: props.kioskId,
        serviceId: props.serviceId,
        studentId: props.studentId,
        id: props.waitingRoomQueueId,
        scheduleBlockId: props.scheduleBlockId,
        appointmentId: props.appointmentId
      })
    });
  };

  const getTimePlural = (conditionToPlural) => {
    return conditionToPlural ? 's' : '';
  };

  const formatWaitingTimeMessage = (hours, minutes, minutesPlural) => {
    if (hours === 0 && minutes === 0) {
      return "less than a minute";
    } else if (hours === 0) {
      return `${minutes} minute${minutesPlural}`;
    } else {
      return `${hours} hour${getTimePlural(hours > 1)} and ${minutes} minute${minutesPlural}`
    }
  };

  const getTimeWaitingMessage = (waitTimeInMinutes) => {
    const {hours, minutes} = getTimeInHours(waitTimeInMinutes);
    return formatWaitingTimeMessage(hours, minutes, getTimePlural(minutes > 1));
  };

  const getGreeting = () => {
    const mainGreeting = `Hi, ${props.studentName}!`;
    const rest = `You have been here for ${getTimeWaitingMessage(props.waitTime)}.`;
    return `${mainGreeting} ${rest}`;
  };

  // Show or hide extra options
  const canViewWaitList = () => canViewWaitListKiosk(props.kioskType);
  const canCheckIntoAnotherService = () =>
    !props.virtualSignInForService && !isSelfService(props.kioskType) && !props.isSingleService;
  const hasMoreOptions = canCheckIntoAnotherService() || canViewWaitList();
  
  return (
    <div className={css['main']}>
      <TextHeader
        id="checkedInOptionsSelection"
        upperText={getGreeting()}
        lowerText={props.mainHeader}
      />
      <div className={css['buttons-container']} aria-labelledby="checkedInOptionsSelection">
        <StyledButton inverted onClick={props.goToWaitListConfirmation}>
          No, keep me checked in
        </StyledButton>
        <StyledButton onClick={onCheckoutClick}>
          Yes, check out
        </StyledButton>
      </div>
      {hasMoreOptions && <div className={css['rectangle']}>
        <div className={css['more-options']}>More Options</div>
        {canCheckIntoAnotherService() && <OptionButton
          text="Check into another Service"
          icon="compare_arrows"
          onClick={props.goToServiceSelection}
        />}
        {canViewWaitList() && <OptionButton
          text="View the wait list"
          icon="hourglass_empty"
          onClick={props.goToWaitList}
        />}
      </div>}
    </div>
  );
};

CheckedInOptions.propTypes = {
  studentName: PropTypes.string.isRequired,
  goToWaitList: PropTypes.func.isRequired,
  goToWaitListConfirmation: PropTypes.func.isRequired,
  goToServiceSelection: PropTypes.func.isRequired,
  studentId: PropTypes.number.isRequired,
  kioskId: PropTypes.number.isRequired,
  setError: PropTypes.func.isRequired,
  setWarning: PropTypes.func.isRequired,
  serviceId: PropTypes.number.isRequired,
  goToCheckoutConfirmation: PropTypes.func.isRequired,
  appointmentId: PropTypes.number.isRequired,
  waitingRoomQueueId: PropTypes.number.isRequired,
  scheduleBlockId: PropTypes.number.isRequired,
  waitTime: PropTypes.number,
  kioskType: PropTypes.string.isRequired,
  mainHeader: PropTypes.string,
  isSingleService: PropTypes.bool.isRequired,
  virtualSignInForService: PropTypes.bool.isRequired
};

export default CheckedInOptions;
